import { useEffect, useState } from 'react';
import type { SearchResults } from '@peruselab/api-client/moleculeSearch';
import { computeMolecularFormula } from '@peruselab/structure-editor/chemistry/moleculeFormula';
import { fetchMolecule } from '../components/moleculeCache';
import type { MoleculeFormulas } from '../components/panels/searchResultsModel';

// Molecular formula per molecule id referenced by the current search results, resolved lazily.
// A result card with no title falls back to this formula for its name (ADR 0007 P6d); the
// underlying molecule fetch is shared with the card thumbnails via `moleculeCache`. Formulas
// never change for a given id, so they are memoised module-wide across searches and mounts.
const formulaCache = new Map<string, Promise<string>>();

function resolveFormula(moleculeId: string): Promise<string> {
  const cached = formulaCache.get(moleculeId);
  if (cached) return cached;
  const pending = fetchMolecule(moleculeId).then((molecule) => computeMolecularFormula(molecule.document));
  pending.catch(() => formulaCache.delete(moleculeId));
  formulaCache.set(moleculeId, pending);
  return pending;
}

function moleculeIdsOf(results: SearchResults): ReadonlySet<string> {
  return new Set<string>(
    [
      ...results.molecules.map((r) => r.moleculeId),
      ...results.drawings.map((r) => r.sourceMoleculeId),
      ...results.reactions.map((r) => r.sourceMoleculeId),
      ...results.illustrations.map((r) => r.sourceMoleculeId)
    ].filter((id): id is string => !!id)
  );
}

export function useMoleculeFormulas(currentResults: SearchResults | null): MoleculeFormulas {
  const [formulas, setFormulas] = useState<MoleculeFormulas>(new Map());

  useEffect(() => {
    if (!currentResults) {
      setFormulas(new Map());
      return;
    }
    let cancelled = false;
    for (const id of moleculeIdsOf(currentResults)) {
      void resolveFormula(id)
        .then((formula) => {
          if (cancelled) return;
          setFormulas((current) => {
            if (current.get(id) === formula) return current;
            const next = new Map(current);
            next.set(id, formula);
            return next;
          });
        })
        .catch(() => {
          // Leave it unresolved; the card keeps showing its id.
        });
    }
    return () => {
      cancelled = true;
    };
  }, [currentResults]);

  return formulas;
}
